// src/lib/thermo/eos/PR.js
// Peng–Robinson EOS (classic PR alpha), vdW-1 mixing with optional kij,
// fugacity coefficients (phi_i), cubic Z roots, and departure enthalpy.
//
// Exports:
//   - solvePR({ P, T, x, comps, kij })
//       -> { ZL, ZV, phiL, phiV, hdepL, hdepV, a_i, b_i, a_mix, b_mix }
//
// Units: T[K], P[Pa], Tc[K], Pc[Pa], omega[-]. R = 8.314462618 J/mol/K.

const R = 8.314462618;

// ---- PR alpha and its d(alpha)/dT ----
// kappa_PR = 0.37464 + 1.54226 ω − 0.26992 ω^2
function kappaPR(omega) {
  const w = Math.max(-0.15, Math.min(1.2, omega ?? 0));
  return 0.37464 + 1.54226 * w - 0.26992 * w * w;
}
function alphaPR(T, Tc, omega) {
  const Tr = Math.max(1e-12, T / Tc);
  const k = kappaPR(omega);
  const g = 1 + k * (1 - Math.sqrt(Tr));
  return g * g;
}
function dalpha_dT_PR(T, Tc, omega) {
  const Tr = Math.max(1e-12, T / Tc);
  const k = kappaPR(omega);
  const g = 1 + k * (1 - Math.sqrt(Tr));
  const dg_dT = (k * -0.5) / (Math.sqrt(Tr) * Tc);
  return 2 * g * dg_dT; // d(g^2)/dT = 2 g dg/dT
}

// ---- Pure (a,b) with PR alpha ----
function purePRParams(comp, T) {
  const { Tc, Pc, omega } = comp;
  const a0 = (0.45724 * R * R * Tc * Tc) / Pc;
  const b = (0.0778 * R * Tc) / Pc;
  const alpha = alphaPR(T, Tc, omega);
  return { a: a0 * alpha, b, a0, alpha };
}

// ---- vdW-1 mixing with kij ----
function mixParams(z, a_i, b_i, kij) {
  const n = z.length;
  let a_mix = 0;
  let b_mix = 0;
  for (let i = 0; i < n; i++) b_mix += z[i] * b_i[i];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      a_mix += z[i] * z[j] * Math.sqrt(a_i[i] * a_i[j]) * (1 - kij_ij);
    }
  }
  return { a_mix, b_mix };
}

// ---- PR cubic solve: Z^3 - (1-B)Z^2 + (A - 3B^2 - 2B)Z - (AB - B^2 - B^3) = 0 ----
function solveCubicPR(A, B) {
  const c2 = -(1 - B);
  const c1 = A - 3 * B * B - 2 * B;
  const c0 = -(A * B - B * B - B * B * B);
  return solveCubicReal(1, c2, c1, c0)
    .filter((Z) => isFinite(Z) && Z > 0)
    .sort((a, b) => a - b);
}
function solveCubicReal(a, b, c, d) {
  b /= a;
  c /= a;
  d /= a;
  const Q = (3 * c - b * b) / 9;
  const Rr = (9 * b * c - 27 * d - 2 * b * b * b) / 54;
  const D = Q * Q * Q + Rr * Rr;
  const out = [];
  if (D >= 0) {
    const S = Math.cbrt(Rr + Math.sqrt(D));
    const T = Math.cbrt(Rr - Math.sqrt(D));
    out.push(-b / 3 + (S + T));
  } else {
    const th = Math.acos(Rr / Math.sqrt(-Q * Q * Q));
    const r = 2 * Math.sqrt(-Q);
    out.push(r * Math.cos(th / 3) - b / 3);
    out.push(r * Math.cos((th + 2 * Math.PI) / 3) - b / 3);
    out.push(r * Math.cos((th + 4 * Math.PI) / 3) - b / 3);
  }
  return out;
}

// ---- Fugacity coefficients (phi_i) at Z ----
function fugacityCoefficientsAtZ(Z, z, a_i, b_i, a_mix, b_mix, A, B, kij) {
  const n = z.length;
  const sqrt_ai = a_i.map((ai) => Math.sqrt(ai));

  // a_ij including kij
  const a_ij = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      a_ij[i][j] = sqrt_ai[i] * sqrt_ai[j] * (1 - kij_ij);
    }
  }
  // da_mix/dn_i ~ 2 Σ z_j a_ij
  const da_mix = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    let s = 0;
    for (let j = 0; j < n; j++) s += z[j] * a_ij[i][j];
    da_mix[i] = 2 * s;
  }

  const phi = new Array(n);
  for (let i = 0; i < n; i++) {
    const bi_b = b_i[i] / b_mix;
    // Standard PR ln(phi_i):
    //   ln(phi_i) = (b_i/b)(Z-1) - ln(Z-B)
    //              - (A/(2√2 B)) * ( 2 Σ_j x_j a_ij / a - b_i/b ) * ln( (Z+(1+√2)B)/(Z+(1-√2)B) )
    // NOTE: Do NOT include an extra "-ln(1 - B/Z)" term; that double-counts ln(Z-B)
    // and blows up φ near the liquid root (Z≈B), causing K-values to explode.
    const ln1 = bi_b * (Z - 1) - Math.log(Math.max(1e-12, Z - B));
    const lnArg = Math.max(
      (Z + (1 + Math.SQRT2) * B) / Math.max(Z + (1 - Math.SQRT2) * B, 1e-12),
      1e-12
    );
    const ln2 =
      (A / (2 * Math.SQRT2 * B)) *
      (da_mix[i] / a_mix - b_i[i] / b_mix) *
      Math.log(lnArg);
    const lnPhi = ln1 - ln2; // subtract the PR attraction term
    phi[i] = Math.exp(Math.max(-50, Math.min(50, lnPhi)));
  }
  return phi;
}

// ---- Departure enthalpy (J/mol) for PR at Z ----
function departureEnthalpyAtZ(
  T,
  P,
  Z,
  z,
  a_i,
  b_i,
  a_mix,
  b_mix,
  A,
  B,
  comps,
  kij
) {
  // h_dep = R T (Z − 1) + (T da_mix/dT − a_mix)/(2√2 b_mix) * ln((Z+(1+√2)B)/(Z+(1−√2)B))
  const n = z.length;

  // da_i/dT via PR alpha
  const a0_i = new Array(n);
  const da_i = new Array(n);
  for (let i = 0; i < n; i++) {
    const { Tc, Pc, omega } = comps[i];
    a0_i[i] = (0.45724 * R * R * Tc * Tc) / Pc;
    da_i[i] = a0_i[i] * dalpha_dT_PR(T, Tc, omega);
  }

  // da_mix/dT (vdW-1 with kij)
  let da_mix = 0;
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const kij_ij = kij ? kij[i]?.[j] ?? 0 : 0;
      const term =
        0.5 *
        (da_i[i] / a_i[i] + da_i[j] / a_i[j]) *
        Math.sqrt(a_i[i] * a_i[j]) *
        (1 - kij_ij);
      da_mix += z[i] * z[j] * term;
    }
  }

  const lnArg = Math.log(
    Math.max(
      1e-12,
      (Z + (1 + Math.SQRT2) * B) / Math.max(Z + (1 - Math.SQRT2) * B, 1e-12)
    )
  );
  const term1 = R * T * (Z - 1);
  const term2 = ((T * da_mix - a_mix) / (2 * Math.SQRT2 * b_mix)) * lnArg;
  return term1 + term2;
}

// ---- Public: solve PR mixture ----
export function solvePR({ P, T, x, comps, kij = null }) {
  const n = x.length;
  const a_i = new Array(n);
  const b_i = new Array(n);

  for (let i = 0; i < n; i++) {
    const { a, b } = purePRParams(comps[i], T);
    a_i[i] = a;
    b_i[i] = b;
  }

  const { a_mix, b_mix } = mixParams(x, a_i, b_i, kij);
  const A = (a_mix * P) / (R * R * T * T);
  const B = (b_mix * P) / (R * T);

  const epsZ = 1e-9;
  const rootsAll = solveCubicPR(A, B).filter((Z) => Number.isFinite(Z));
  const roots = rootsAll.filter((Z) => Z > Math.max(0, B + epsZ)).slice().sort((a, b) => a - b);
  let singlePhase = false;
  let Zsingle = null;
  let ZL, ZV;
  if (roots.length >= 2) {
    ZL = roots[0];
    ZV = roots[roots.length - 1];
  } else if (roots.length === 1) {
    singlePhase = true;
    Zsingle = roots[0];
    ZL = ZV = Zsingle;
  } else {
    singlePhase = true;
    const Zcand = rootsAll.length ? Math.max(...rootsAll) : 1.0;
    Zsingle = Math.max(Math.max(1e-6, B + 1e-6), Zcand);
    ZL = ZV = Zsingle;
  }
  if (!singlePhase) {
    const rel = Math.abs(ZV - ZL) / Math.max(1e-12, Math.abs(ZV));
    if (rel < 1e-8) {
      singlePhase = true;
      Zsingle = 0.5 * (ZL + ZV);
      ZL = ZV = Zsingle;
    }
  }

    if (singlePhase) {
    const phi = fugacityCoefficientsAtZ(
      Zsingle,
      x,
      a_i,
      b_i,
      a_mix,
      b_mix,
      A,
      B,
      kij
    );
    return {
      singlePhase: true,
      Z: Zsingle,
      ZL: Zsingle,
      ZV: Zsingle,
      phiL: phi,
      phiV: phi,
      hdepL: 0,
      hdepV: 0,
      a_i,
      b_i,
      a_mix,
      b_mix,
    };
  }

const phiL = fugacityCoefficientsAtZ(
    ZL,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    kij
  );
  const phiV = fugacityCoefficientsAtZ(
    ZV,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    kij
  );

  const hdepL = departureEnthalpyAtZ(
    T,
    P,
    ZL,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    comps,
    kij
  );
  const hdepV = departureEnthalpyAtZ(
    T,
    P,
    ZV,
    x,
    a_i,
    b_i,
    a_mix,
    b_mix,
    A,
    B,
    comps,
    kij
  );

  return { ZL, ZV, phiL, phiV, hdepL, hdepV, a_i, b_i, a_mix, b_mix };
}
